<template>
  <nav v-if="pageCount > 1" class="common-pagination">
    <button
      class="page-button arrow"
      :disabled="page <= 1"
      aria-label="Page précédente"
      @click="goTo(page - 1)"
    >
      <CommonIcon name="ChevronLeft" :size="22" :stroke-width="1.5" />
    </button>
    <template v-for="(item, index) in pages" :key="index">
      <span v-if="item === null" class="ellipsis">…</span>
      <button
        v-else
        class="page-button"
        :class="{ active: item === page }"
        @click="goTo(item)"
      >
        {{ item }}
      </button>
    </template>
    <button
      class="page-button arrow"
      :disabled="page >= pageCount"
      aria-label="Page suivante"
      @click="goTo(page + 1)"
    >
      <CommonIcon name="ChevronRight" :size="22" :stroke-width="1.5" />
    </button>
  </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import CommonIcon from './CommonIcon.vue'

const props = defineProps<{
  page: number
  totalItems: number
  itemsPerPage: number
}>()

const emit = defineEmits<{
  (e: 'update:page', page: number): void
}>()

const pageCount = computed(() => Math.max(1, Math.ceil(props.totalItems / props.itemsPerPage)))

const pages = computed<(number | null)[]>(() => {
  const count = pageCount.value
  if (count <= 7) {
    return Array.from({ length: count }, (_, i) => i + 1)
  }

  const items: (number | null)[] = [1]
  const start = Math.max(2, props.page - 1)
  const end = Math.min(count - 1, props.page + 1)
  if (start > 2) items.push(null)
  for (let i = start; i <= end; i++) items.push(i)
  if (end < count - 1) items.push(null)
  items.push(count)

  return items
})

function goTo(page: number) {
  if (page < 1 || page > pageCount.value || page === props.page) return
  emit('update:page', page)
}
</script>

<style scoped lang="scss">
.common-pagination {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

.page-button {
  transition: 600ms cubic-bezier(0.19, 1, 0.22, 1);
  cursor: pointer;
  min-width: 35px;
  height: 35px;
  padding: 0 5px;
  border-radius: 6px;
  border: 1.5px solid rgba(var(--vcrgb-dark), 0.2);
  background-color: var(--vc-white);
  color: var(--vc-dark);
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: 'Roboto';
  font-weight: 500;
}

.page-button:hover {
  background-color: rgba(var(--vcrgb-dark), 0.1);
}

.page-button.arrow {
  min-width: 45px;
  height: 45px;
  background-color: var(--vc-white);
}

.page-button.arrow:disabled {
  border: 0;
  background-color: var(--vc-light);
}

.page-button.active {
  background-color: var(--vc-light);
  border: 0;
  pointer-events: none;
}

.page-button:disabled {
  color: rgba(var(--vcrgb-dark), 0.3);
  pointer-events: none;
}

.ellipsis {
  color: rgba(var(--vcrgb-dark), 0.5);
  padding: 0 2px;
}
</style>
